import { useForm } from 'react-hook-form'
import { Surgery } from 'common/types'
import { useEditSurgeryMutation, useGetSurgeriesQuery } from 'lib/apollo/hooks'
import { useGetSurgeons } from 'lib/services/api/reports-service/preference-card/getSurgeons'
import { useGetProcedureTypes } from 'lib/services/api/reports-service/preference-card/getProcedureTypes'
import { enqueueSnackbar } from 'notistack'
import { useEffect } from 'react'
import { EditProcedureFormValues } from './EditProcedureModal.types'
import { useApolloClients } from 'lib/apollo/apolloProvider'
import { useEnterpriseStore } from 'store'

export const useEditProcedureModalLogic = (
  surgery: Surgery,
  onClose: () => void,
  beforeDate?: Date,
  afterDate?: Date
) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<EditProcedureFormValues>({
    defaultValues: {
      patientFirstName: surgery?.patient?.firstName ?? null,
      patientLastName: surgery?.patient?.lastName ?? null,
      surgeon: surgery?.visit?.attendingProvider
        ? {
            id: surgery.visit?.attendingProvider?._id,
            name: `${surgery.visit?.attendingProvider?.firstName} ${surgery.visit?.attendingProvider?.lastName}`,
          }
        : null,
      procedure: surgery?.procedures?.[0]?.description || '',
      patientDob: surgery?.patient?.dateOfBirth || null,
      mrNumber: surgery?.patient?.idMR || null,
    },
  })

  const { iservicesClient } = useApolloClients()
  const [editSurgery] = useEditSurgeryMutation(iservicesClient)

  const { selectedDepartment, isDepartmentsLoaded } = useEnterpriseStore()
  const { refetch: refetchSurgeries } = useGetSurgeriesQuery(
    {
      before: beforeDate,
      after: afterDate,
      limit: 0,
      departmentId: selectedDepartment?.id,
    },
    {
      disablePolling: true,
      skip: !isDepartmentsLoaded,
    }
  )
  useGetSurgeriesQuery(
    {
      departmentId: selectedDepartment?.id,
    },
    {
      skip: !isDepartmentsLoaded,
    }
  )
  const { data: rawSurgeons, isLoading: isLoadingSurgeons } = useGetSurgeons()
  const { data: rawProcedures, isLoading: isLoadingProcedures } =
    useGetProcedureTypes()

  const surgeons =
    rawSurgeons?.surgeons?.map((item) => ({
      id: item.id,
      name: `${item.firstName} ${item.lastName}`,
    })) ?? []

  const procedures =
    rawProcedures?.procedures?.map((item) => ({
      id: item,
      name: item,
    })) ?? []

  const onSubmit = async (data: EditProcedureFormValues) => {
    try {
      const response = await editSurgery({
        variables: {
          input: {
            surgeryId: surgery._id,
            patientId: surgery?.patient?._id ?? undefined,
            surgeonId: String(data?.surgeon?.id) ?? undefined,
            procedureName: data?.procedure,
            patientDob: data?.patientDob
              ? data.patientDob.replace(/\//g, '-')
              : undefined,
            idMR: data?.mrNumber ?? undefined,
            patientFirstName: data.patientFirstName ?? undefined,
            patientLastName: data.patientLastName ?? undefined,
          },
        },
      })
      if (response.data?.editSurgery.success) {
        refetchSurgeries()
        enqueueSnackbar('Procedure updated successfully', {
          variant: 'success',
        })
        onClose()
      } else {
        enqueueSnackbar('Failed to update procedure', { variant: 'error' })
      }
    } catch (error) {
      console.error('Error updating surgery:', error)
      enqueueSnackbar('Error updating procedure', { variant: 'error' })
    }
  }

  const handleOnSubmit = () => {
    handleSubmit(onSubmit)()
  }

  useEffect(() => {
    if (surgery) {
      setValue('patient', {
        id: surgery.patient._id,
        name: `${surgery.patient.firstName} ${surgery.patient.lastName}`,
      })
      setValue('surgeon', {
        id: surgery.visit?.attendingProvider?._id || '',
        name: `${surgery.visit?.attendingProvider?.firstName} ${surgery.visit?.attendingProvider?.lastName}`,
      })
      setValue('procedure', surgery.procedures?.[0]?.description || '')
      setValue('patientDob', surgery.patient?.dateOfBirth || null)
      setValue('mrNumber', surgery.patient?.idMR || null)
    }
  }, [setValue, surgery])

  return {
    control,
    errors,
    handleSubmit,
    onSubmit,
    surgeons,
    procedures,
    isLoadingSurgeons,
    isLoadingProcedures,
    isLoadingUpdate: false,
    handleOnSubmit,
  }
}
